/**
 * Menu Calendar Component
 * Monthly calendar view for admin menu management
 */

import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CheckCircle2, CircleDashed, Megaphone, Unlink, Users } from 'lucide-react';
import {
  getMonthDates,
  isSunday,
  isToday,
  toISODate,
  formatDate,
  getDayOfWeek,
  getMonthRange,
} from '@/lib/utils/dateHelpers';
import type { Menu } from '@/types/api';
import { RECIPE_CATEGORY_LABELS } from '@/types/api';
import { RSVP_SIZES, type RSVPSizeCode } from '@/types/units';

interface MenuCalendarProps {
  month: Date;
  menus: Menu[];
  onDateClick: (date: Date, menu?: Menu) => void;
}

type RecipeBadgeVariant = 'list' | 'cell';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getCount(menu: Menu, prefix: 'rsvpCount' | 'memberRsvpCount' | 'publicRsvpCount', size: RSVPSizeCode) {
  return Number((menu as unknown as Record<string, number | undefined>)[`${prefix}${size}`] ?? 0);
}

function formatCounts(menu: Menu, prefix: 'rsvpCount' | 'memberRsvpCount' | 'publicRsvpCount') {
  return RSVP_SIZES
    .map((size) => `${size} ${getCount(menu, prefix, size)}`)
    .join(', ');
}

function hasCounts(menu: Menu, prefix: 'rsvpCount' | 'memberRsvpCount' | 'publicRsvpCount') {
  return RSVP_SIZES.some((size) => getCount(menu, prefix, size) > 0);
}

/**
 * MenuCalendar - Monthly grid for viewing and managing menus
 */
export function MenuCalendar({
  month,
  menus,
  onDateClick,
}: MenuCalendarProps) {
  // Create a map of date -> menu for quick lookup
  const menusByDate = useMemo(() => {
    const map = new Map<string, Menu>();
    for (const menu of menus) {
      map.set(menu.date, menu);
    }
    return map;
  }, [menus]);

  // Get all dates in the month
  const monthDates = useMemo(() => getMonthDates(month), [month]);

  // Calculate padding for the first week
  const { start } = getMonthRange(month);
  const firstDayOfWeek = getDayOfWeek(start);

  // Create empty cells for days before the month starts
  const leadingEmptyDays = Array(firstDayOfWeek).fill(null);

  return (
    <div className="space-y-4">
      {/* Desktop Calendar View */}
      <div className="hidden md:block">
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAYS.map((day) => (
            <div
              key={day}
              className="text-center text-sm font-medium text-muted-foreground py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for padding */}
          {leadingEmptyDays.map((_, index) => (
            <div key={`empty-${index}`} className="h-[130px]" />
          ))}

          {/* Date cells */}
          {monthDates.map((date) => (
            <CalendarCell
              key={toISODate(date)}
              date={date}
              menu={menusByDate.get(toISODate(date))}
              onDateClick={onDateClick}
            />
          ))}
        </div>
      </div>

      {/* Mobile List View */}
      <div className="md:hidden space-y-2">
        {monthDates.map((date) => {
          const menu = menusByDate.get(toISODate(date));
          const sunday = isSunday(date);
          const today = isToday(date);

          return (
            <Card
              key={toISODate(date)}
              className={cn(
                'p-4 cursor-pointer hover:bg-accent/50 transition-colors',
                today && 'border-primary',
                sunday && 'bg-muted/30'
              )}
              onClick={() => onDateClick(date, menu)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={cn(
                        'font-medium',
                        today && 'text-primary'
                      )}
                    >
                      {formatDate(date, 'EEE, MMM d')}
                    </span>
                    {today && (
                      <Badge variant="outline" className="text-xs">
                        Today
                      </Badge>
                    )}
                    {menu && <MenuStatusIcon menu={menu} />}
                  </div>

                  {sunday ? (
                    <span className="text-sm text-muted-foreground italic">
                      No FMB
                    </span>
                  ) : menu ? (
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{menu.title}</span>
                        <RecipeBadge menu={menu} variant="list" />
                        <AudienceBadge menu={menu} variant="list" />
                      </div>
                      {menu.assignedCatererNameSnapshot && (
                        <p className="text-xs text-muted-foreground">
                          Caterer: {menu.assignedCatererNameSnapshot}
                        </p>
                      )}
                      {hasCounts(menu, 'rsvpCount') ? (
                        <span className="text-xs text-muted-foreground ml-2">
                          ({formatCounts(menu, 'rsvpCount')})
                        </span>
                      ) : null}
                      {menu.rsvpAudience === 'JAMAAT_WIDE' &&
                       hasCounts(menu, 'publicRsvpCount') && (
                        <p className="text-xs text-muted-foreground">
                          Public: {formatCounts(menu, 'publicRsvpCount')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      No menu
                    </span>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Internal Components
// ============================================================================

interface CalendarCellProps {
  date: Date;
  menu?: Menu;
  onDateClick: (date: Date, menu?: Menu) => void;
}

function CalendarCell({
  date,
  menu,
  onDateClick,
}: CalendarCellProps) {
  const sunday = isSunday(date);
  const today = isToday(date);
  const content = (
    <Card
      className={cn(
        'h-[130px] p-2 transition-colors flex flex-col',
        menu ? 'gap-1' : 'gap-6',
        !sunday && 'cursor-pointer hover:bg-accent/50',
        sunday && 'cursor-default',
        today && 'border-primary border-2',
        sunday && 'bg-muted/30'
      )}
      onClick={() => !sunday && onDateClick(date, menu)}
    >
      {/* Date Number + Status Icon */}
      <div className="flex items-center justify-between flex-shrink-0">
        <span
          className={cn(
            'text-sm font-medium',
            today && 'text-primary',
            sunday && 'text-muted-foreground'
          )}
        >
          {formatDate(date, 'd')}
        </span>
        <div className="flex items-center gap-1">
          {today && (
            <Badge variant="outline" className="text-xs">
              Today
            </Badge>
          )}
          {menu?.type === 'MENU' && menu.rsvpAudience === 'JAMAAT_WIDE' ? (
            <Users className="h-3.5 w-3.5 text-emerald-700" aria-label="Jamaat-wide menu" />
          ) : null}
          {menu && <MenuStatusIcon menu={menu} />}
        </div>
      </div>

      {/* Content */}
      <div className={cn(
        'flex-1 overflow-hidden',
        menu ? 'mt-1' : 'mt-0.5'
      )}>
        {sunday ? (
          <p className="text-xs text-muted-foreground italic">Sunday</p>
        ) : menu ? (
          <div>
            <p className="text-xs font-medium line-clamp-2">{menu.title}</p>
            <div className="flex flex-wrap gap-1">
              <RecipeBadge menu={menu} variant="cell" />
            </div>
            {menu.assignedCatererNameSnapshot && (
              <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                {menu.assignedCatererNameSnapshot}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            + Add menu
          </p>
        )}
      </div>
    </Card>
  );

  if (!menu || sunday || menu.type !== 'MENU') {
    return content;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>{content}</div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-56">
        <CalendarCellTooltip menu={menu} />
      </TooltipContent>
    </Tooltip>
  );
}

function RecipeBadge({ menu, variant }: { menu: Menu; variant: RecipeBadgeVariant }) {
  if (menu.recipes && menu.recipes.length > 0) {
    if (variant === 'cell') {
      // In calendar cells: show first recipe category + count if multiple
      const first = menu.recipes[0];
      if (!first) return null;
      return (
        <span className="inline-block text-[10px] px-1.5 py-0.5 mt-1 rounded-full bg-sky-50 text-sky-600">
          {RECIPE_CATEGORY_LABELS[first.category]}
          {menu.recipes.length > 1 && ` +${menu.recipes.length - 1}`}
        </span>
      );
    }
    // In list view: show all recipe categories as badges
    return (
      <span className="inline-flex gap-1 flex-wrap">
        {menu.recipes.map((r) => (
          <span
            key={r.menuRecipeId}
            className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-600"
          >
            {RECIPE_CATEGORY_LABELS[r.category]}
          </span>
        ))}
      </span>
    );
  }

  if (menu.type !== 'MENU') {
    return null;
  }

  return (
    <span
      className={cn(
        'rounded-full bg-amber-50 text-amber-600',
        variant === 'list'
          ? 'text-xs px-2 py-0.5 flex items-center gap-1'
          : 'inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 mt-1'
      )}
    >
      <Unlink className={variant === 'list' ? 'h-3 w-3' : 'h-2.5 w-2.5'} />
      No recipe
    </span>
  );
}

function AudienceBadge({ menu, variant }: { menu: Menu; variant: RecipeBadgeVariant }) {
  if (menu.type !== 'MENU' || menu.rsvpAudience !== 'JAMAAT_WIDE') {
    return null;
  }

  return (
    <span
      className={cn(
        'rounded-full bg-emerald-50 text-emerald-700',
        variant === 'list'
          ? 'text-xs px-2 py-0.5 inline-flex items-center gap-1'
          : 'inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 mt-1'
      )}
    >
      <Users className={variant === 'list' ? 'h-3 w-3' : 'h-2.5 w-2.5'} />
      100% Thaali
    </span>
  );
}

function CalendarCellTooltip({ menu }: { menu: Menu }) {
  return (
    <div className="space-y-1">
      {menu.rsvpAudience === 'JAMAAT_WIDE' ? (
        <p className="font-medium text-foreground">100% Thaali</p>
      ) : null}
      <p>Member RSVPs: {formatCounts(menu, 'memberRsvpCount')}</p>
      {menu.rsvpAudience === 'JAMAAT_WIDE' ? (
        <p>Public RSVPs: {formatCounts(menu, 'publicRsvpCount')}</p>
      ) : null}
    </div>
  );
}

/**
 * Status icon for menu - shown in date header row
 */
function MenuStatusIcon({ menu }: { menu: Menu }) {
  // Announcement icon (megaphone) - color based on status
  if (menu.type === 'ANNOUNCEMENT') {
    return (
      <Megaphone
        className={cn(
          'h-4 w-4',
          menu.status === 'PUBLISHED' ? 'text-green-600' : 'text-yellow-600'
        )}
      />
    );
  }

  // Regular menu status icons
  switch (menu.status) {
    case 'PUBLISHED':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case 'DRAFT':
      return <CircleDashed className="h-4 w-4 text-yellow-600" />;
    case 'ARCHIVED':
      return <CircleDashed className="h-4 w-4 text-gray-400" />;
    default:
      return null;
  }
}
